import React from 'react';
import ReactMarkdown from 'react-markdown';

const MarkdownRenderer = ({ content }) => {
  // Preprocess content to handle line breaks and bullet points properly
  const preprocessContent = (text) => {
    if (!text) return '';
    
    return text
      // Convert all line breaks to \n first
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      
      // Handle lists - look for sections starting with "Other nearby locations:" or similar headers
      .replace(/^(.*locations:)\n((?:^[^•\n-][^\n]*\n?)+)/gm, (match, header, items) => {
        // Split items and add bullet points
        const bulletItems = items
          .trim()
          .split('\n')
          .map(item => `* ${item.trim()}`)
          .join('\n');
        return `${header}\n\n${bulletItems}`;
      })
      
      // Convert existing bullet points
      .replace(/[●○•]/g, '*')
      
      // Ensure proper spacing around bullet points
      .replace(/\n\*/g, '\n* ')
      .replace(/\n {2}\*/g, '\n  * ')
      
      // Handle double line breaks for paragraphs
      .replace(/\n\n+/g, '\n\n')
      
      .trim();
  };

  const processedContent = preprocessContent(content);

  const markdownComponents = {
    // Text elements
    p: ({ children }) => (
      <p className="mb-2 last:mb-0 text-gray-700 break-words">{children}</p>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-700">{children}</em>
    ),
    
    // Lists with improved styling
    ul: ({ children }) => (
      <ul className="list-disc list-outside mb-2 space-y-1 text-gray-700 pl-6">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-outside mb-2 space-y-1 text-gray-700 pl-6">{children}</ol>
    ),
    li: ({ children }) => (
      <li className="text-gray-700 pl-1">{children}</li>
    ),
    
    // Headings
    h1: ({ children }) => (
      <h1 className="text-xl font-bold text-gray-900 mb-3 mt-4">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-lg font-bold text-gray-900 mb-2 mt-3">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-base font-bold text-gray-900 mb-2 mt-2">{children}</h3>
    ),
    
    // Code elements
    code: ({ children, inline }) => (
      inline ? (
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800">
          {children}
        </code>
      ) : (
        <code className="block bg-gray-100 p-3 rounded-lg text-sm font-mono text-gray-800 overflow-x-auto">
          {children}
        </code>
      )
    ),
    pre: ({ children }) => (
      <pre className="mb-4 overflow-x-auto">
        {children}
      </pre>
    ),
    
    // Blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 mb-4">
        {children}
      </blockquote>
    ),
    
    // Links
    a: ({ children, href }) => (
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {children}
      </a>
    ),
    
    // Tables
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full divide-y divide-gray-200">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-50">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-gray-200 bg-white">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr>{children}</tr>
    ),
    th: ({ children }) => (
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {children}
      </td>
    ),
  };

  return (
    <div className="markdown-content">
      <ReactMarkdown components={markdownComponents}>
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer; 